import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Archive, ArchiveRestore, FileText, Loader2, X } from "lucide-react";

export interface ArchivedDocument {
  id: string;
  ids: string[];
  name: string;
  version: number;
  archivedAt: string;
  urls: string[];
  status?: string;
}

export function ArchiveDocumentButton({ onArchive, busy = false }: {
  onArchive: () => Promise<void>;
  busy?: boolean;
}) {
  return (
    <button type="button" onClick={onArchive} disabled={busy}
      className="inline-flex shrink-0 items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-600 transition hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:opacity-50">
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Archive className="h-4 w-4" />}
      Archiver
    </button>
  );
}

export function ArchivedDocuments({ title, documents, onRestore, busy = false }: {
  title: string;
  documents: ArchivedDocument[];
  onRestore: (ids: string[]) => Promise<void>;
  busy?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const dialog = useRef<HTMLDialogElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const headingId = useId();

  useEffect(() => {
    if (!open) return;
    const node = dialog.current;
    node?.showModal();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      node?.close();
      document.body.style.overflow = previousOverflow;
      trigger.current?.focus();
    };
  }, [open]);

  return <>
    <button ref={trigger} type="button" onClick={() => setOpen(true)}
      className="inline-flex items-center gap-2 rounded-md px-2 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500">
      <Archive className="h-4 w-4" />{title}
      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs tabular-nums">{documents.length}</span>
    </button>
    {open && createPortal(
      <dialog ref={dialog} aria-labelledby={headingId} onCancel={() => setOpen(false)}
        onClick={(event) => { if (event.target === event.currentTarget) setOpen(false); }}
        className="m-auto max-h-[85dvh] w-[calc(100%_-_2rem)] max-w-xl rounded-xl border border-gray-200 bg-white p-0 text-gray-900 shadow-xl backdrop:bg-black/40">
        <div className="flex max-h-[85dvh] flex-col">
          <div className="flex shrink-0 items-center justify-between gap-4 border-b border-gray-200 px-5 py-4">
            <h2 id={headingId} className="text-lg font-semibold">{title}</h2>
            <button type="button" autoFocus aria-label="Fermer les archives" onClick={() => setOpen(false)}
              className="rounded-md p-2 text-gray-500 hover:bg-gray-100 focus-visible:ring-2 focus-visible:ring-indigo-500"><X className="h-5 w-5" /></button>
          </div>
          <div className="min-h-0 overflow-y-auto p-5">
            <p className="mb-4 text-sm leading-6 text-gray-600">Ces documents sont conservés pour votre équipe. Ils sont masqués côté client et exclus des statistiques et des relances.</p>
            {documents.length === 0 ? <p className="py-8 text-center text-sm text-gray-500">Aucun document archivé.</p> :
              <ul className="space-y-3">{documents.map((item) => <li key={item.id} className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                <div className="flex items-start gap-3">
                  <FileText className="mt-1 h-5 w-5 shrink-0 text-gray-400" />
                  <div className="min-w-0 flex-1">
                    <p className="break-words text-sm font-semibold">{item.name}</p>
                    <p className="mt-1 text-xs text-gray-500">Version {item.version}{item.status ? ` · ${item.status}` : ""}</p>
                    <p className="mt-1 text-xs text-gray-500">Archivé le {new Date(item.archivedAt).toLocaleString("fr-FR", { timeZone: "Europe/Paris", dateStyle: "short", timeStyle: "short" })}</p>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                  <div className="flex flex-wrap gap-3">{item.urls.map((url, index) => <a key={`${url}-${index}`} href={url} target="_blank" rel="noreferrer"
                    className="py-2 text-sm font-medium text-indigo-600 hover:underline">{item.urls.length === 1 ? "Consulter le PDF" : `PDF ${index + 1}`}</a>)}</div>
                  <button type="button" disabled={busy} onClick={() => onRestore(item.ids)}
                    className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium hover:bg-gray-100 focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:opacity-50">
                    <ArchiveRestore className="h-4 w-4" />Restaurer
                  </button>
                </div>
              </li>)}</ul>}
          </div>
          <p className="shrink-0 border-t border-gray-200 px-5 py-3 text-xs leading-5 text-gray-500">Restaurer remet le document à disposition du client et dans les statistiques. Aucune relance n’est redémarrée automatiquement.</p>
        </div>
      </dialog>, document.body)}
  </>;
}
